import { useGetContractedCompanies } from "@/entities/get-contracted-companies/fetch";
import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { useMultipleGetVehicle } from "@/entities/get-vehicle/fetch";
import { useGetVehicles } from "@/entities/get-vehicles/fetch";
import { usePostOffersCargos } from "@/entities/post-offers-cargos/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { excludeNull } from "@/shared/lib/collection";
import { fetchFileFromUrl } from "@/shared/lib/file";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { type Loadable, transformLoadable } from "@/shared/lib/loadable";
import { safeParseInt } from "@/shared/lib/number";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useNavigate } from "@tanstack/react-router";
import { Result } from "neverthrow";
import { useCallback, useMemo, useState } from "react";
import type {
  AdminDeliveryProposalCreatePageProps,
  DeliveryProposalOfferCargoDestination,
  MemberVehicle,
} from "../model";

export const useAdminDeliveryProposalCreatePageProps = (
  offerCargoIdStr: string | undefined,
): AdminDeliveryProposalCreatePageProps => {
  // Auth
  const { session } = useAuth();

  // State
  const [previewValues, setPreviewValues] =
    useState<AdminDeliveryProposalCreatePageProps["previewValues"]>();

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      AdminDeliveryProposalCreatePageProps["defaultInputValues"]
    >("CarrierDeliveryProposalCreatePage");

  // Backend API
  const offerCargoId = useMemo(() => {
    if (!offerCargoIdStr) {
      return undefined;
    }
    const id = safeParseInt(offerCargoIdStr);
    if (!id) {
      return undefined;
    }
    return id;
  }, [offerCargoIdStr]);
  const getDraftOfferCargo = useGetOffersCargo(
    {
      id: offerCargoId ?? -1,
    },
    {
      enabled: !!offerCargoId,
    },
  );

  // 企業一覧取得
  const getCompaniesQuery = useGetContractedCompanies(
    {
      pagination: { displayLimit: 10000 },
    },
    { enabled: true },
  );

  const postOffersCargos = usePostOffersCargos();

  const getVehicles = useGetVehicles(
    {
      search: {
        companyId: session.isAuthenticated ? session.user.companyId : undefined,
      },
      pagination: {
        displayLimit: 10000,
      },
    },
    {
      enabled: session.isAuthenticated,
    },
  );
  // workaround: getVehiclesにはequipmentsが入っていないので単品APIで取り直す
  const getMultipleVehicles = useMultipleGetVehicle(
    Object.fromEntries(
      getVehicles.data?.data.vehicles.map((vehicle) => [
        vehicle.id,
        { id: vehicle.id },
      ]) ?? [],
    ),
    {
      enabled: !!getVehicles.data?.data.vehicles,
    },
  );

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Props
  const draftOfferCargo = useMemo<
    AdminDeliveryProposalCreatePageProps["draftOfferCargo"]
  >(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getDraftOfferCargo),
      (offerCargo) => {
        return offerCargo.data;
      },
    );
  }, [getDraftOfferCargo]);

  // 企業一覧の変換
  const companies = useMemo(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getCompaniesQuery),
      (data) =>
        data.data.companies.filter((c) => c.category?.includes("carrier")),
    );
  }, [getCompaniesQuery]);

  // Actions
  const navigate = useNavigate();
  const onInputValuesUpdate = useCallback<
    AdminDeliveryProposalCreatePageProps["onInputValuesUpdate"]
  >(
    (deliveryProposalOfferCargo) => {
      setInProgressValues(deliveryProposalOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    AdminDeliveryProposalCreatePageProps["onConfirmClick"]
  >(
    async (deliveryProposalOfferCargo) => {
      if (!deliveryProposalOfferCargo.vehicleId) {
        setPreviewValues({
          ...deliveryProposalOfferCargo,
          vehicle: {
            status: "success",
            data: undefined,
          },
        });
      } else {
        const getDeliveryProposalOfferCargoVehicle =
          deliveryProposalOfferCargo.vehicleId
            ? getMultipleVehicles[deliveryProposalOfferCargo.vehicleId]
            : null;
        if (!getDeliveryProposalOfferCargoVehicle) {
          console.error(
            `vehicle not found ${deliveryProposalOfferCargo.vehicleId}`,
          );
          return;
        }
        const vehicle = transformLoadable(
          loadableFromUseQueryResult(getDeliveryProposalOfferCargoVehicle),
          (vehicle) => vehicle.data,
        );
        setPreviewValues({
          ...deliveryProposalOfferCargo,
          vehicle,
        });
      }
    },
    [getMultipleVehicles],
  );

  const onBackToInProgressClick = useCallback<
    AdminDeliveryProposalCreatePageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(undefined);
  }, []);

  const onSubmitClick = useCallback<
    AdminDeliveryProposalCreatePageProps["onSubmitClick"]
  >(async () => {
    if (!previewValues) {
      return;
    }
    const getDraftOfferCargoData = getDraftOfferCargo.data?.data;
    if (!getDraftOfferCargoData) {
      return;
    }
    const destinations = getDraftOfferCargoData.destinations.map<
      (typeof getDraftOfferCargoData.destinations)[0]
    >((destination, index) => {
      let proposalDestination:
        | DeliveryProposalOfferCargoDestination
        | undefined = undefined;
      if (index === 0) {
        proposalDestination = previewValues.loadingDestination;
      } else if (index === getDraftOfferCargoData.destinations.length - 1) {
        proposalDestination = previewValues.unloadingDestination;
      } else {
        proposalDestination = previewValues.transitDestinations[index - 1];
      }
      if (!proposalDestination) {
        return destination;
      }
      return {
        ...destination,
        deadlineType:
          proposalDestination.deadlineType ?? destination.deadlineType,
        deadlineTime:
          proposalDestination.deadlineTime ?? destination.deadlineTime,
        deadlineTimeEnd:
          proposalDestination.deadlineTimeEnd ?? destination.deadlineTimeEnd,
        taskDetail: proposalDestination.taskDetail ?? destination.taskDetail,
        memo: proposalDestination.memo ?? destination.memo,
      };
    });
    const destinationFilesResults = await Promise.all(
      excludeNull(
        getDraftOfferCargoData.attachedDestinationFiles?.map((file) => {
          if (!file.url || !file.filename) {
            return null;
          }
          return fetchFileFromUrl(file.url, file.filename);
        }) ?? [],
      ),
    );
    const destinationFiles = Result.combine(destinationFilesResults);
    if (destinationFiles.isErr()) {
      setGlobalNotification({
        variant: "error",
        message:
          "申し訳ありません。配送提案に失敗しました。ページをリロードしてもう一度お試しください。",
      });
      return;
    }
    const deliveryRequestFilesResults = await Promise.all(
      excludeNull(
        getDraftOfferCargoData.attachedDeliveryRequestFiles?.map((file) => {
          if (!file.url || !file.filename) {
            return null;
          }
          return fetchFileFromUrl(file.url, file.filename);
        }) ?? [],
      ),
    );
    const deliveryRequestFiles = Result.combine(deliveryRequestFilesResults);
    if (deliveryRequestFiles.isErr()) {
      setGlobalNotification({
        variant: "error",
        message:
          "申し訳ありません。配送提案に失敗しました。ページをリロードしてもう一度お試しください。",
      });
      return;
    }
    const destinationsWithSplitAddress = destinations.map((destination) => {
      if (!destination.address)
        return { ...destination, city: "", addressLine: "" };

      const parts = destination.address.split(" ");
      const city = parts.length > 1 ? parts[0] : destination.address;
      const addressLine =
        parts.length > 1 ? parts.slice(1).join(" ") : undefined;

      return {
        ...destination,
        city,
        addressLine,
      };
    });

    // companyIdを文字列から数値に変換
    const companyId = previewValues.companyId
      ? safeParseInt(previewValues.companyId)
      : null;

    const response = await postOffersCargos.mutateAsync({
      data: {
        ...getDraftOfferCargoData,
        ...previewValues,
        companyId,
        stage: "delivery_proposal",
        processId: getDraftOfferCargoData.process.id,
        sourceOfferCargoId: getDraftOfferCargoData.id,
        destinations: destinationsWithSplitAddress,
        destinationFiles: destinationFiles.value,
        deliveryRequestFiles: deliveryRequestFiles.value,
        vehicleId: previewValues.vehicleId ?? null,
      },
    });
    if (response && isSuccessStatusCode(response.status)) {
      setGlobalNotification({
        variant: "success",
        message: "配送提案が完了しました",
        autoHide: true,
      });
      removeInProgressValues();
      navigate({
        to: "/admin/cargos",
        search: {
          tab: "proposed",
        },
      });
    } else {
      setGlobalNotification({
        variant: "error",
        message: "配送提案に失敗しました",
      });
    }
  }, [
    getDraftOfferCargo.data,
    postOffersCargos.mutateAsync,
    removeInProgressValues,
    setGlobalNotification,
    navigate,
    previewValues,
    previewValues?.loadingDestination,
    previewValues?.unloadingDestination,
    previewValues?.vehicleId,
    previewValues?.shippingFee,
    previewValues?.memo,
  ]);

  const vehicles = useMemo<Loadable<MemberVehicle[]>>(() => {
    const vehicleLoadbles = Object.values(getMultipleVehicles).map(
      (vehicleQuery) => {
        return transformLoadable(
          loadableFromUseQueryResult(vehicleQuery),
          (vehicle) => vehicle.data,
        );
      },
    );
    if (vehicleLoadbles.some((vehicle) => vehicle.status === "error")) {
      return {
        status: "error",
        error: {
          message: vehicleLoadbles
            .filter((vehicle) => vehicle.status === "error")
            .map((vehicle) => vehicle.error?.message)
            .join(", "),
        },
      };
    }
    if (vehicleLoadbles.some((vehicle) => vehicle.status === "loading")) {
      return {
        status: "loading",
        data: undefined,
      };
    }
    return {
      status: "success",
      data: excludeNull(vehicleLoadbles.map((vehicle) => vehicle.data)),
    };
  }, [getMultipleVehicles]);
  return {
    draftOfferCargo,
    companies,
    defaultInputValues: inProgressValues,
    vehicles,
    previewValues,
    onInputValuesUpdate,
    onBackToInProgressClick,
    onSubmitClick,
    onConfirmClick,
  };
};
